// API Service for communicating with the backend
// PERMANENT URLs - ALWAYS CONNECT TO AWS, NEVER LOCALHOST BACKEND
import { getApiBaseUrl as getPermanentApiBaseUrl, getAuthApiBaseUrl } from '../config/permanent-urls';
import { tokenRefreshService } from './tokenRefreshService';
import { sendDebugLog } from '../utils/debugLogging'
import { createInsufficientTokensError, handleInsufficientTokensError } from '../utils/insufficientTokensHandler'

const getApiBaseUrl = () => {
  // ALWAYS use permanent URLs - these are the source of truth
  // ALWAYS connect to AWS - never localhost backend
  return getPermanentApiBaseUrl();
};

const API_BASE_URL = getApiBaseUrl();

const getAuthApiBaseUrlLocal = () => {
  // ALWAYS use permanent auth URLs - these are the source of truth
  // ALWAYS connect to AWS - never localhost backend
  return getAuthApiBaseUrl();
};

const AUTH_API_BASE_URL = getAuthApiBaseUrlLocal();

// Force rebuild - deployment timestamp: 2024-09-28 14:40:00

export interface Profile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  updatedAt: string;
  phone?: string;
  linkedinUrl?: string;
  resumeUrl?: string;
  summary?: string;
  skills?: string[];
  generatedSummary?: string;
  generatedSkills?: string[];
  experience?: string;
  education?: string;
  industry?: string;
  totalResumes?: number;
  processedResumes?: number;
  lastLoginAt?: string;
  isActive?: boolean;
  emailVerified?: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

export interface LoginResponse {
  success: boolean;
  data?: {
    accessToken: string;  // Backend returns camelCase
    idToken: string;      // Backend returns camelCase (REQUIRED for API Gateway)
    access_token?: string;  // Legacy snake_case support
    id_token?: string;      // Legacy snake_case support
    profile: Profile;
  };
  error?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface ResumeItem {
  id: string;
  profileId?: string;
  originalFilename?: string;
  fileSize?: number;
  fileType?: string;
  resumeType?: string;
  storagePath?: string;
  uploadedAt?: string;
  atsProcessingStatus?: string;
  atsScore?: number | null;
  // Optional detailed ATS payload from backend list API
  atsData?: any;
  // Derived/summarized metrics for UI convenience
  atsComplianceScorePct?: number | null; // 0-100 percent, derived from atsData.atsComplianceScore (0-1)
  atsDataId?: string;
  lastProcessedAt?: string | null;
  // Fields from actual API response
  filename?: string;
  file_size?: number;
  file_type?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  processed_at?: string | null;
  s3_key?: string;
  url?: string;
  is_active?: boolean;
  // Legacy fields for backward compatibility
  name?: string;
  fileName?: string;
  uploadDate?: string;
}

export interface UploadResumeResponse {
  success: boolean;
  data: ResumeItem;
  message?: string;
}

export interface ApplyAnalysisSuggestionsResponse {
  success: boolean;
  data?: {
    newResumeId: string;
    filename?: string;
    downloadUrl?: string;
    resumeStatus?: string;
    resume?: ResumeItem;
    appliedSuggestionIds?: string[];
    skippedSuggestionIds?: string[];
  };
  error?: string;
}

export interface ApplyAnalysisSuggestionsAiResponse {
  success: boolean;
  data?: {
    newResumeId: string;
    filename?: string;
    downloadUrl?: string;
    resumeStatus?: string;
    resume?: ResumeItem;
    appliedSuggestionIds?: string[];
    skippedSuggestionIds?: string[];
  };
  error?: string;
}

export interface ResumeStatusResponse {
  status: string;
  atsProcessingStatus?: string;
  atsScore?: number | null;
  fileSize?: number | null;
  data?: any;
}

export interface PresignRequest {
  filename: string;
  contentType: string;
  fileSize: number;
  checksumSHA256: string;
}

export interface PresignResponse {
  presignedUrl: string;
  s3Key: string;
  expiresIn: number;
  message: string;
}

class ApiService {
  private baseUrl: string;
  private token: string | null = null;

  constructor() {
    this.baseUrl = API_BASE_URL;
    // Load token from localStorage on initialization
    this.token = localStorage.getItem('token');
  }

  private createHeaders(extra?: HeadersInit, includeJson: boolean = true): Headers {
    const headers = new Headers();
    if (includeJson) {
      headers.set('Content-Type', 'application/json');
    }

    // Always check localStorage for token - it might have been updated elsewhere
    const token = this.token || localStorage.getItem('token');
    if (token) {
      // Update this.token if it was null but localStorage has it
      if (!this.token && token) {
        this.token = token;
      }
      headers.set('Authorization', `Bearer ${token}`);
    }

    if (extra) {
      new Headers(extra).forEach((value, key) => {
        headers.set(key, value);
      });
    }

    return headers;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    let hasRetriedAfter401 = false;

    // Proactively refresh the token if it's expired or expiring soon,
    // so the first attempt goes out with a valid token instead of hitting 401.
    const preCheckToken = this.token || localStorage.getItem('token');
    if (preCheckToken && tokenRefreshService.isTokenExpiredOrExpiringSoon(preCheckToken)) {
      await tokenRefreshService.forceRefresh().catch(() => {});
    }

    const includeJsonHeader = !(options.body instanceof FormData);
    const headers = this.createHeaders(options.headers, includeJsonHeader);

    const config: RequestInit = {
      ...options,
      headers,
      credentials: 'omit',
    };

    try {
      let response = await fetch(url, config);

      // Handle 401 errors with token refresh
      if (response.status === 401 && !hasRetriedAfter401) {
        hasRetriedAfter401 = true;
        try {
          const refreshed = await tokenRefreshService.forceRefresh();
          if (!refreshed) {
            throw new Error('Token refresh failed');
          }
          // Retry the request with new token
          const newHeaders = this.createHeaders(options.headers, includeJsonHeader);
          const newConfig: RequestInit = {
            ...options,
            headers: newHeaders,
            credentials: 'omit',
          };
          response = await fetch(url, newConfig);
        } catch (refreshError) {
          // Refresh failed - clear token and redirect to login
          this.clearToken();
          // Redirect to login if not already there
          if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
            // #region agent log
            sendDebugLog({
              location: 'apiService.ts:237',
              message: 'Redirecting to login (refresh error)',
              data: { endpoint, currentPath: window.location.pathname, hypothesisId: 'C' },
              timestamp: Date.now(),
              sessionId: 'debug-session',
              runId: 'run1'
            });
            // #endregion
            window.location.href = '/login';
          }
          throw new Error('Authentication required. Please log in again.');
        }
      }
      // For auth endpoints (login/register), let the actual error message pass through
      // Don't clear token or throw generic error - let the backend's specific error be shown

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        const tokenError = createInsufficientTokensError(errorData, response.status);
        if (tokenError) {
          handleInsufficientTokensError(tokenError);
          throw tokenError;
        }

        if (errorData.detail && typeof errorData.detail === 'object' && 'message' in errorData.detail) {
          const detail = errorData.detail as { message?: string; error_code?: string; field?: string };
          const error = new Error(detail.message || 'Request failed');
          (error as any).errorCode = detail.error_code;
          (error as any).field = detail.field;
          (error as any).status = response.status; // Include status code for error handling
          throw error;
        }

        const errorMessage = (errorData && typeof errorData === 'object' && 'detail' in errorData) 
          ? (errorData.detail as string)
          : (errorData && typeof errorData === 'object' && 'error' in errorData)
          ? (errorData.error as string)
          : `HTTP ${response.status}: ${response.statusText}`;
        const error = new Error(errorMessage);
        (error as any).status = response.status; // Include status code for error handling
        throw error;
      }

      const text = await response.text();
      if (!text) {
        return {} as T;
      }

      const parsedData = JSON.parse(text);
      return parsedData;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  private async authRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${AUTH_API_BASE_URL}${endpoint}`;

    const includeJsonHeader = !(options.body instanceof FormData);
    const headers = this.createHeaders(options.headers, includeJsonHeader);

    const config: RequestInit = {
      ...options,
      headers,
      credentials: 'omit',
    };

    try {
      const response = await fetch(url, config);
      const text = await response.text();
      let parsedData: any = null;
      if (text) {
        try {
          parsedData = JSON.parse(text);
        } catch {
          parsedData = text;
        }
      }

      if (!response.ok) {
        const errorMessage =
          (parsedData && typeof parsedData === 'object' && (parsedData.error || parsedData.message)) ||
          (typeof parsedData === 'string' && parsedData) ||
          `HTTP error! status: ${response.status}`;
        const error = new Error(errorMessage);
        (error as any).status = response.status;
        throw error;
      }

      return (parsedData ?? {}) as T;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.authRequest<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    // Store token if login was successful
    // CRITICAL: API Gateway Cognito User Pool authorizer requires id_token, not access_token
    if (response.success) {
      // Handle both camelCase (from backend) and snake_case (legacy) formats
      const token_for_api = response.data?.idToken || response.data?.id_token || 
                           response.data?.accessToken || response.data?.access_token;
      if (token_for_api) {
        this.setToken(token_for_api);
      }
    }
    
    return response;
  }

  async register(userData: RegisterRequest): Promise<LoginResponse> {
    const response = await this.authRequest<LoginResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    // Store token
    // CRITICAL: API Gateway Cognito User Pool authorizer requires id_token, not access_token
    // Handle both camelCase (from backend) and snake_case (legacy) formats
    const token_for_api = response.data?.idToken || response.data?.id_token || 
                         response.data?.accessToken || response.data?.access_token;
    if (token_for_api) {
      this.setToken(token_for_api);
    }
    return response;
  }

  async logout(): Promise<void> {
    this.clearToken();
  }

  async verifyToken(): Promise<any> {
    return this.request('/auth/verify-token', { method: 'GET' });
  }

  // Profile methods
  async getProfile(): Promise<Profile> {
    return this.request<Profile>('/profiles/me', { method: 'GET' });
  }

  async updateProfile(profileData: Partial<Profile>): Promise<Profile> {
    return this.request<Profile>('/profiles/me', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  // Authentication methods
  async verifyEmail(email: string, code: string): Promise<any> {
    const normalizedEmail = email.trim();
    const normalizedCode = code.trim();
    if (!normalizedEmail || !normalizedCode) {
      throw new Error('Email and verification code are required');
    }
    return this.authRequest('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({
        email: normalizedEmail,
        code: normalizedCode,
        // Backward/forward compatibility with handlers that expect verificationCode
        verificationCode: normalizedCode,
      }),
    });
  }

  async forgotPassword(email: string): Promise<any> {
    return this.authRequest('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyResetToken(token: string): Promise<any> {
    return this.authRequest('/auth/verify-reset-token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<any> {
    return this.authRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<any> {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  // Resume methods
  async getResumes(): Promise<any> {
    return this.request('/resumes', { method: 'GET' });
  }

  async getResumeStatus(resumeId: string): Promise<ResumeStatusResponse> {
    return this.request<ResumeStatusResponse>(`/resumes/${resumeId}/status`, { method: 'GET' });
  }

  /**
   * Get resume analysis results
   * 
   * IMPORTANT: Path is /resumes/analysis/{id}, NOT /resumes/{id}/analysis
   * This is due to API Gateway limitation: cannot use {id} as sibling of {proxy+} routes.
   * See infrastructure/cloudformation/templates/kairos-phase5-api-workers.yaml lines 645-647 and backend lambda_handler.py for details.
   * 
   * @param resumeId - The resume ID to get analysis for
   * @returns Analysis results or null if not found
   */
  async getResumeAnalysis(resumeId: string): Promise<any> {
    return this.request(`/resumes/analysis/${resumeId}`, { method: 'GET' });
  }

  /**
   * Start resume analysis
   * 
   * IMPORTANT: Path is /resumes/analyze/{id}, NOT /resumes/{id}/analyze
   * This is due to API Gateway limitation: cannot use {id} as sibling of {proxy+} routes.
   * See infrastructure/cloudformation/templates/kairos-phase5-api-workers.yaml lines 645-647 and backend lambda_handler.py for details.
   * 
   * @param resumeId - The resume ID to analyze
   * @returns Response with analysis_status: 'processing' | 'completed' | 'failed'
   */
  async analyzeResume(resumeId: string): Promise<any> {
    return this.request(`/resumes/analyze/${resumeId}`, { method: 'POST' });
  }

  async getPresignedUrl(request: PresignRequest): Promise<PresignResponse> {
    return this.request<PresignResponse>('/resumes/presigned-url', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async createResume(data: { s3Key: string; filename: string; fileSize?: number; fileType?: string; resumeType?: string }): Promise<any> {
    return this.request('/resumes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async processResume(resumeId: string): Promise<any> {
    return this.request(`/resumes/${resumeId}/process`, {
      method: 'POST',
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  async deleteResume(resumeId: string): Promise<any> {
    return this.request(`/resumes/${resumeId}`, {
      method: 'DELETE',
    });
  }

  async applyAnalysisSuggestions(
    resumeId: string,
    analysisId: string,
    selectedSuggestionIds: string[],
  ): Promise<ApplyAnalysisSuggestionsResponse> {
    const response = await this.request<ApplyAnalysisSuggestionsResponse>(
      `/resumes/${resumeId}/analysis/${analysisId}/apply`,
      {
        method: 'POST',
        body: JSON.stringify({ selectedSuggestionIds }),
      }
    );
    return response;
  }

  async applyAnalysisSuggestionsAi(
    resumeId: string,
    analysisId: string,
    selectedSuggestionIds: string[],
  ): Promise<ApplyAnalysisSuggestionsAiResponse> {
    const response = await this.request<ApplyAnalysisSuggestionsAiResponse>(
      `/resumes/${resumeId}/analysis/${analysisId}/apply-ai`,
      {
        method: 'POST',
        body: JSON.stringify({ selectedSuggestionIds }),
      }
    );
    return response;
  }

  // Job Applications
  async saveJobApplication(jobApplication: any): Promise<any> {
    return this.request('/job-applications', {
      method: 'POST',
      body: JSON.stringify(jobApplication),
    });
  }

  async getJobApplications(limit?: number, lastEvaluatedKey?: string): Promise<any> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (lastEvaluatedKey) params.append('lastEvaluatedKey', lastEvaluatedKey);
    
    const queryString = params.toString();
    const url = queryString ? `/job-applications?${queryString}` : '/job-applications';
    
    return this.request(url, { method: 'GET' });
  }

  async getJobApplicationById(jobApplicationId: string): Promise<any> {
    return this.request(`/job-applications/${jobApplicationId}`, { method: 'GET' });
  }

  async updateJobApplication(jobApplicationId: string, updateData: any): Promise<any> {
    return this.request(`/job-applications/${jobApplicationId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  async deleteJobApplication(jobApplicationId: string): Promise<any> {
    return this.request(`/job-applications/${jobApplicationId}`, { method: 'DELETE' });
  }

  // Resume download URL
  async getResumeDownloadUrl(resumeId: string): Promise<any> {
    const response = await this.request(`/resumes/${resumeId}/download`, { method: 'GET' });
    
    // Backend returns: { success: true, downloadUrl: "...", expiresIn: 300 }
    // The downloadUrl is at the top level, not in a data property
    const typedResponse = response as { success?: boolean; downloadUrl?: string; download_url?: string; data?: { downloadUrl?: string; download_url?: string } };
    
    return typedResponse;
  }

  // ATS Data fetching
  async getAtsData(resumeId: string): Promise<any> {
    const response = await this.request(`/resumes/${resumeId}/ats`, { method: 'GET' });
    
    const typedResponse = response as ApiResponse<Record<string, any>>;
    return typedResponse.data || {};
  }

  // Support Requests (no authentication required)
  async submitSupportRequest(data: {
    type: 'service_request' | 'suggestion' | 'bug_report';
    subject: string;
    description: string;
    email: string;
    name: string;
    profileId?: string;
  }): Promise<any> {
    // Support requests don't require authentication - make request without Authorization header
    // baseUrl already includes /api/v1, so we just need /support-requests
    const url = `${this.baseUrl}/support-requests`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      credentials: 'omit',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData?.detail || errorData?.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  // Token management
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('token', token);
  }

  clearToken(): void {
    this.token = null;
    localStorage.removeItem('token');
  }

  getToken(): string | null {
    return this.token;
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }
}

const apiServiceInstance = new ApiService();

export const apiService = apiServiceInstance;

export default apiServiceInstance;

export const __testHooks = {
	setToken: (token: string) => apiServiceInstance.setToken(token),
	clearToken: () => apiServiceInstance.clearToken(),
}
